import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useTranslation } from '@ibk/shared-translation';
import { sortCardTrackingEventsNewestFirst } from '../../../../utils/mapCardTrackingDetailStatus';
import styles from './Timeline.module.css';
const MINIMUM_COLUMN_WIDTH = 120;
const MINIMUM_COLUMN_WIDTH_RATIO = 0.5;
const KEYBOARD_RESIZE_STEP = 16;
const MOBILE_BREAKPOINT = '(max-width: 768px)';
const WIDTH_TOLERANCE = 0.01;
const createEqualColumnWidths = (totalWidth, columnCount) => Array.from({ length: columnCount }, () => totalWidth / columnCount);
const sumColumnWidths = (widths) => widths.reduce((total, width) => total + width, 0);
const scaleColumnWidths = (widths, totalWidth) => {
    const currentTotal = sumColumnWidths(widths);
    if (currentTotal <= 0) {
        return createEqualColumnWidths(totalWidth, widths.length);
    }
    return widths.map(width => (width * totalWidth) / currentTotal);
};
const resolveMinimumColumnWidth = (totalWidth, columnCount) => Math.min(MINIMUM_COLUMN_WIDTH, (totalWidth / columnCount) * MINIMUM_COLUMN_WIDTH_RATIO);
const distributeColumnWidth = (widths, columnIndex, requestedWidth) => {
    const trailingIndexes = widths.map((_, index) => index).filter(index => index > columnIndex);
    if (trailingIndexes.length === 0) {
        return widths;
    }
    const totalWidth = sumColumnWidths(widths);
    const minimumWidth = resolveMinimumColumnWidth(totalWidth, widths.length);
    const equalWidth = totalWidth / widths.length;
    const trailingSlack = trailingIndexes.reduce((slack, index) => slack + Math.max(0, widths[index] - minimumWidth), 0);
    const nextWidths = [...widths];
    const targetWidth = Math.min(Math.max(requestedWidth, minimumWidth), widths[columnIndex] + trailingSlack);
    if (targetWidth > widths[columnIndex]) {
        let pendingDelta = targetWidth - widths[columnIndex];
        trailingIndexes.forEach(index => {
            const transferable = Math.min(pendingDelta, Math.max(0, nextWidths[index] - minimumWidth));
            nextWidths[index] -= transferable;
            nextWidths[columnIndex] += transferable;
            pendingDelta -= transferable;
        });
        return nextWidths;
    }
    let freedWidth = widths[columnIndex] - targetWidth;
    nextWidths[columnIndex] = targetWidth;
    trailingIndexes.forEach(index => {
        const restorable = Math.min(freedWidth, Math.max(0, equalWidth - nextWidths[index]));
        nextWidths[index] += restorable;
        freedWidth -= restorable;
    });
    if (freedWidth > WIDTH_TOLERANCE) {
        nextWidths[trailingIndexes[0]] += freedWidth;
    }
    return nextWidths;
};
export function Timeline({ events }) {
    const { t } = useTranslation();
    const tableRef = useRef(null);
    const removePointerListenersRef = useRef(null);
    const hasCustomWidthsRef = useRef(false);
    const totalWidthRef = useRef(0);
    const [isMobile, setIsMobile] = useState(false);
    const [columnWidths, setColumnWidths] = useState([]);
    const sortedEvents = useMemo(() => sortCardTrackingEventsNewestFirst(events), [events]);
    const hasObservations = sortedEvents.some(event => Boolean(event.observation));
    const columns = useMemo(() => [
        {
            headerTestId: 'textViewTimelineHeaderDateTime',
            key: 'dateTime',
            label: t('cards.tracking.timeline.dateTime'),
            resizeTestId: 'buttonViewTimelineResizeDateTime',
        },
        {
            headerTestId: 'textViewTimelineHeaderEvent',
            key: 'event',
            label: t('cards.tracking.timeline.event'),
            resizeTestId: 'buttonViewTimelineResizeEvent',
        },
        {
            headerTestId: 'textViewTimelineHeaderDetails',
            key: 'details',
            label: t('cards.tracking.timeline.details'),
            resizeTestId: 'buttonViewTimelineResizeDetails',
        },
        ...(hasObservations
            ? [
                {
                    headerTestId: 'textViewTimelineHeaderObservation',
                    key: 'observation',
                    label: t('cards.tracking.timeline.observation'),
                    resizeTestId: 'buttonViewTimelineResizeObservation',
                },
            ]
            : []),
    ], [hasObservations, t]);
    const columnCount = columns.length;
    const gridTemplateColumns = columnWidths.length === columnCount
        ? columnWidths.map(width => `${width}px`).join(' ')
        : `repeat(${columnCount}, minmax(0, 1fr))`;
    const gridStyle = isMobile ? undefined : { gridTemplateColumns };
    const formatDateTime = (dateString) => {
        const date = new Date(dateString);
        return format(date, "dd / MM / yyyy 'às' HH:mm", { locale: ptBR });
    };
    const measureTotalWidth = () => {
        const availableWidth = tableRef.current?.getBoundingClientRect().width ?? 0;
        if (availableWidth > 0) {
            totalWidthRef.current = availableWidth;
        }
        return totalWidthRef.current;
    };
    const getEqualColumnWidth = () => measureTotalWidth() / columnCount;
    const resizeColumn = (columnIndex, requestedWidth) => {
        hasCustomWidthsRef.current = true;
        setColumnWidths(currentWidths => {
            const baseWidths = currentWidths.length === columnCount
                ? currentWidths
                : createEqualColumnWidths(totalWidthRef.current, columnCount);
            return distributeColumnWidth(baseWidths, columnIndex, requestedWidth);
        });
    };
    const handlePointerDown = (event, columnIndex) => {
        event.preventDefault();
        removePointerListenersRef.current?.();
        const initialPointerX = event.clientX;
        const initialWidth = columnWidths[columnIndex] ?? getEqualColumnWidth();
        const handlePointerMove = (pointerEvent) => {
            resizeColumn(columnIndex, initialWidth + pointerEvent.clientX - initialPointerX);
        };
        const removePointerListeners = () => {
            globalThis.removeEventListener('pointermove', handlePointerMove);
            globalThis.removeEventListener('pointerup', removePointerListeners);
            removePointerListenersRef.current = null;
        };
        globalThis.addEventListener('pointermove', handlePointerMove);
        globalThis.addEventListener('pointerup', removePointerListeners);
        removePointerListenersRef.current = removePointerListeners;
    };
    const handleResizeKeyDown = (event, columnIndex) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
            return;
        }
        event.preventDefault();
        const currentWidth = columnWidths[columnIndex] ?? getEqualColumnWidth();
        resizeColumn(columnIndex, currentWidth + (event.key === 'ArrowRight' ? KEYBOARD_RESIZE_STEP : -KEYBOARD_RESIZE_STEP));
    };
    const resetColumnWidths = () => {
        hasCustomWidthsRef.current = false;
        setColumnWidths(createEqualColumnWidths(measureTotalWidth(), columnCount));
    };
    useEffect(() => {
        if (typeof globalThis.matchMedia !== 'function') {
            return;
        }
        const mediaQuery = globalThis.matchMedia(MOBILE_BREAKPOINT);
        const updateMobileState = () => setIsMobile(mediaQuery.matches);
        updateMobileState();
        mediaQuery.addEventListener('change', updateMobileState);
        return () => mediaQuery.removeEventListener('change', updateMobileState);
    }, []);
    useEffect(() => {
        hasCustomWidthsRef.current = false;
        if (isMobile) {
            setColumnWidths([]);
            return;
        }
        const applyTotalWidth = () => {
            const availableWidth = tableRef.current?.getBoundingClientRect().width ?? 0;
            if (availableWidth <= 0) {
                return;
            }
            totalWidthRef.current = availableWidth;
            setColumnWidths(currentWidths => hasCustomWidthsRef.current && currentWidths.length === columnCount
                ? scaleColumnWidths(currentWidths, availableWidth)
                : createEqualColumnWidths(availableWidth, columnCount));
        };
        applyTotalWidth();
        if (typeof ResizeObserver === 'undefined' || !tableRef.current) {
            return;
        }
        const resizeObserver = new ResizeObserver(applyTotalWidth);
        resizeObserver.observe(tableRef.current);
        return () => resizeObserver.disconnect();
    }, [columnCount, isMobile]);
    useEffect(() => () => {
        removePointerListenersRef.current?.();
    }, []);
    return (_jsx("div", { className: styles.timelineContainer, "data-testid": 'viewTimelineContainer', children: _jsxs("div", { className: styles.timelineTable, "data-testid": 'viewTimelineTable', ref: tableRef, children: [_jsx("div", { className: styles.timelineHeader, "data-testid": 'viewTimelineHeader', style: gridStyle, children: columns.map((column, columnIndex) => (_jsxs("div", { className: styles.headerCell, "data-testid": column.headerTestId, children: [column.label, !isMobile && columnIndex < columnCount - 1 && (_jsx("button", { "aria-label": t('cards.tracking.timeline.resizeColumn', { column: column.label }), className: styles.resizeHandle, "data-testid": column.resizeTestId, onDoubleClick: resetColumnWidths, onKeyDown: event => handleResizeKeyDown(event, columnIndex), onPointerDown: event => handlePointerDown(event, columnIndex), type: 'button' }))] }, column.key))) }), _jsx("div", { className: styles.timelineBody, "data-testid": 'viewTimelineBody', children: sortedEvents.map((event, index) => (_jsxs("div", { className: `${styles.timelineRow}
              ${index % 2 === 0 ? styles.timelineRowEven : styles.timelineRowOdd}`, "data-testid": `viewTimelineRow${index}`, style: gridStyle, children: [_jsx("div", { className: styles.timelineCell, "data-testid": `textViewTimelineCellDateTime${index}`, children: formatDateTime(event.occurredAt) }), _jsx("div", { className: `${styles.timelineCell} ${styles.statusCell}`, "data-testid": `textViewTimelineCellStatus${index}`, children: event.trackingStatusGroupName }), _jsx("div", { className: styles.timelineCell, "data-testid": `textViewTimelineCellDescription${index}`, children: event.trackingStatusName }), hasObservations && (_jsx("div", { className: styles.timelineCell, "data-testid": `textViewTimelineCellActivity${index}`, children: event.observation }))] }, `${event.trackingStatusId}-${event.occurredAt}`))) })] }) }));
}
